<template>
  <div class="flex items-center gap-3">
    <!-- Theme Version Selector -->
    <themed-select
      :value="selectedTheme"
      :options="themeOptions"
      @input="handleThemeChange"
      aria-label="Select theme version"
    />

    <!-- Light/Dark Mode Toggle (only visible when V1 is selected) -->
    <theme-toggle v-if="selectedTheme === 'v1'"></theme-toggle>
    
    <!-- Screen reader announcement for theme version changes -->
    <div 
      role="status" 
      aria-live="polite" 
      aria-atomic="true" 
      class="sr-only"
    >
      {{ announcement }}
    </div>
  </div>
</template>

<script>
import ThemedSelect from './v1/ThemedSelect.vue';

// KAN-173: Module-level flag — only one POST per page load regardless of
// how many ThemeSelector instances mount (prevents the double-fire bug).
let _themeSyncedThisPageLoad = false;

export default {
  name: 'ThemeSelector',
  components: {
    ThemedSelect
  },
  data() {
    return {
      selectedTheme: 'none',
      announcement: '',
      themeOptions: [
        { label: 'Theme: None', value: 'none' },
        { label: 'Theme: V1', value: 'v1' }
      ]
    }
  },
  mounted() {
    // Check saved theme version preference
    const savedThemeVersion = localStorage.getItem('reopt_theme_version') || 'v1';
    this.selectedTheme = savedThemeVersion;
    
    // Sync cookie with localStorage on mount
    document.cookie = `reopt_theme_version=${savedThemeVersion}; path=/; max-age=31536000`; // 1 year
    
    // KAN-173: Only POST once per page load — guard against multiple ThemeSelector instances
    if (!_themeSyncedThisPageLoad) {
      _themeSyncedThisPageLoad = true;
      axios.post('/user/theme-preference', {
        theme_version: savedThemeVersion
      }).catch(error => {
        console.error('Failed to sync theme preference to server on mount:', error);
      });
    }
    
    this.applyThemeVersion();
    
    // Listen for theme changes from other instances
    this.$eventHub.$on('theme-version-changed', this.syncThemeVersion);
    window.addEventListener('storage', this.syncFromStorage);
  },
  
  beforeDestroy() {
    this.$eventHub.$off('theme-version-changed', this.syncThemeVersion);
    window.removeEventListener('storage', this.syncFromStorage);
  },
  methods: {
    syncThemeVersion(themeVersion) {
      // Update this instance's selected theme from other instances
      if (themeVersion !== undefined && this.selectedTheme !== themeVersion) {
        this.selectedTheme = themeVersion;
        // Don't call applyThemeVersion here as it's already applied by the initiating instance
      }
    },
    
    syncFromStorage(event) {
      if (event.key === 'reopt_theme_version') {
        const newTheme = event.newValue || 'none';
        if (this.selectedTheme !== newTheme) {
          this.selectedTheme = newTheme;
          this.applyThemeVersion();
        }
      }
    },
    
    handleThemeChange(newTheme) {
      const oldTheme = localStorage.getItem('reopt_theme_version');
      
      // Update selected theme
      this.selectedTheme = newTheme;
      
      // Only proceed if theme actually changed
      if (oldTheme === newTheme) {
        return;
      }
      
      localStorage.setItem('reopt_theme_version', this.selectedTheme);
      
      // Set cookie so server can detect theme preference
      document.cookie = `reopt_theme_version=${this.selectedTheme}; path=/; max-age=31536000`; // 1 year
      
      // Sync to server session and reload page
      axios.post('/user/theme-preference', {
        theme_version: this.selectedTheme
      }).then(() => {
        // Reload the page to load the correct view from server
        window.location.reload();
      }).catch(error => {
        console.error('Failed to sync theme preference to server:', error);
        // Still apply theme on client side even if server sync fails
        this.applyThemeVersion();
        
        // Broadcast to other ThemeSelector instances
        this.$eventHub.$emit('theme-version-changed', this.selectedTheme);
        
        // Announce theme version change to screen readers
        this.announcement = `Theme ${this.selectedTheme === 'v1' ? 'V1' : 'None'} activated`;
        
        // Clear announcement after screen reader has time to read it
        setTimeout(() => {
          this.announcement = '';
        }, 1000);
      });
    },
    applyThemeVersion() {
      if (this.selectedTheme === 'v1') {
        // Enable V1 theme - theme styles will be active
        document.documentElement.setAttribute('data-theme', 'v1');
        document.documentElement.setAttribute('data-topbar', 'v1');
        
        // Apply saved light/dark mode if exists
        const savedMode = localStorage.getItem('reopt_theme_mode') || 'dark';
        if (savedMode === 'dark') {
          document.documentElement.classList.add('dark');
        }
      } else {
        // Disable theme - remove all theme-related classes
        document.documentElement.removeAttribute('data-theme');
        document.documentElement.setAttribute('data-topbar', 'original');
        document.documentElement.classList.remove('dark');
      }
    }
  }
}
</script>

<style scoped>
/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
